// ============================================================================
// BP-04-02: Documents Frontend - Type Definitions
// ============================================================================

// -----------------------------------------------------------------------------
// Enums
// -----------------------------------------------------------------------------

export enum DocumentType {
  MEDICAL_RECORD = 'medical_record',
  IEP = 'iep',
  LEGAL = 'legal',
  INSURANCE = 'insurance',
  FINANCIAL = 'financial',
  THERAPY = 'therapy',
  SCHOOL = 'school',
  OTHER = 'other',
}

export enum DocumentStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
  DELETED = 'deleted',
}

export enum ViewMode {
  GRID = 'grid',
  LIST = 'list',
}

export enum SortField {
  NAME = 'name',
  DATE = 'date',
  SIZE = 'size',
  TYPE = 'type',
  CREATED = 'created_at',
  UPDATED = 'updated_at',
}

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

// -----------------------------------------------------------------------------
// Document Folder
// -----------------------------------------------------------------------------

export interface DocumentFolder {
  id: string;
  family_id: string;
  parent_id: string | null;
  name: string;
  description: string | null;
  color: string | null;
  icon: string | null;
  display_order: number;
  document_count: number;
  children?: DocumentFolder[];
  created_at: string;
  updated_at: string;
}

export interface DocumentFolderCreate {
  name: string;
  parent_id?: string | null;
  description?: string;
  color?: string;
  icon?: string;
}

export interface DocumentFolderUpdate {
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  display_order?: number;
}

// -----------------------------------------------------------------------------
// Document
// -----------------------------------------------------------------------------

export interface Document {
  id: string;
  family_id: string;
  child_id: string | null;
  folder_id: string | null;
  uploaded_by_id: string;
  uploaded_by_name: string;
  filename: string;
  original_filename: string;
  file_type: string;
  file_size_bytes: number;
  mime_type: string;
  storage_path: string;
  document_type: DocumentType;
  title: string;
  description: string | null;
  document_date: string | null;
  provider_name: string | null;
  tags: string[];
  extracted_text: string | null;
  is_shared_with_attorney: boolean;
  shared_at: string | null;
  is_encrypted: boolean;
  version: number;
  thumbnail_url?: string | null;
  preview_url?: string | null;
  download_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface DocumentCreate {
  folder_id?: string | null;
  child_id?: string | null;
  document_type: DocumentType;
  title: string;
  description?: string;
  document_date?: string;
  provider_name?: string;
  tags?: string[];
}

export interface DocumentUpdate {
  folder_id?: string | null;
  child_id?: string | null;
  document_type?: DocumentType;
  title?: string;
  description?: string;
  document_date?: string;
  provider_name?: string;
  tags?: string[];
}

// -----------------------------------------------------------------------------
// Document Version
// -----------------------------------------------------------------------------

export interface DocumentVersion {
  id: string;
  document_id: string;
  version_number: number;
  filename: string;
  file_size_bytes: number;
  uploaded_by_id: string;
  uploaded_by_name: string;
  change_notes: string | null;
  download_url?: string;
  created_at: string;
}

// -----------------------------------------------------------------------------
// Sharing
// -----------------------------------------------------------------------------

export interface DocumentShare {
  id: string;
  document_id: string;
  shared_with_attorney_id: string;
  attorney_name: string;
  shared_by_id: string;
  shared_by_name: string;
  access_level: 'view' | 'download';
  shared_at: string;
  expires_at: string | null;
  last_accessed_at: string | null;
}

export interface ShareWithAttorneyRequest {
  attorney_id: string;
  access_level?: 'view' | 'download';
  expires_at?: string;
  message?: string;
}

// -----------------------------------------------------------------------------
// Upload
// -----------------------------------------------------------------------------

export interface PresignedUploadUrl {
  upload_url: string;
  document_id: string;
  fields: Record<string, string>;
  expires_at: string;
}

export interface UploadProgress {
  file: File;
  document_id?: string;
  progress: number;
  status: 'pending' | 'uploading' | 'processing' | 'complete' | 'error';
  error?: string;
}

export interface BulkUploadResult {
  successful: Document[];
  failed: Array<{
    filename: string;
    error: string;
  }>;
}

// -----------------------------------------------------------------------------
// Filters & Search
// -----------------------------------------------------------------------------

export interface DocumentFilters {
  folder_id?: string | null;
  child_id?: string | null;
  document_type?: DocumentType | DocumentType[];
  date_from?: string;
  date_to?: string;
  tags?: string[];
  is_shared_with_attorney?: boolean;
  search?: string;
}

export interface DocumentSearchParams extends DocumentFilters {
  page?: number;
  per_page?: number;
  sort_by?: SortField;
  sort_order?: SortOrder;
}

export interface DocumentSearchResult {
  documents: Document[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

// -----------------------------------------------------------------------------
// Bulk Operations
// -----------------------------------------------------------------------------

export interface BulkMoveRequest {
  document_ids: string[];
  target_folder_id: string | null;
}

export interface BulkDeleteRequest {
  document_ids: string[];
}

export interface BulkDownloadRequest {
  document_ids: string[];
}

export interface BulkDownloadResult {
  download_url: string;
  expires_at: string;
  file_count: number;
  total_size_bytes: number;
}

// -----------------------------------------------------------------------------
// Statistics
// -----------------------------------------------------------------------------

export interface DocumentStats {
  total_documents: number;
  total_size_bytes: number;
  by_type: Record<DocumentType, number>;
  by_child: Array<{
    child_id: string;
    child_name: string;
    count: number;
  }>;
  shared_with_attorney: number;
  recent_uploads: number;
}

// -----------------------------------------------------------------------------
// UI State
// -----------------------------------------------------------------------------

export interface DocumentsUIState {
  viewMode: ViewMode;
  selectedDocumentIds: string[];
  expandedFolderIds: string[];
  currentFolderId: string | null;
  filters: DocumentFilters;
  sortBy: SortField;
  sortOrder: SortOrder;
  isUploading: boolean;
  uploadQueue: UploadProgress[];
}

// -----------------------------------------------------------------------------
// Component Props
// -----------------------------------------------------------------------------

export interface DocumentCardProps {
  document: Document;
  isSelected?: boolean;
  onSelect?: (id: string, selected: boolean) => void;
  onView?: (document: Document) => void;
  onDownload?: (document: Document) => void;
  onEdit?: (document: Document) => void;
  onDelete?: (document: Document) => void;
  onMove?: (document: Document) => void;
  onShare?: (document: Document) => void;
}

export interface FolderTreeProps {
  folders: DocumentFolder[];
  selectedFolderId: string | null;
  expandedFolderIds: string[];
  onSelectFolder: (folderId: string | null) => void;
  onToggleExpand: (folderId: string) => void;
  onCreateFolder?: (parentId: string | null) => void;
  onEditFolder?: (folder: DocumentFolder) => void;
  onDeleteFolder?: (folder: DocumentFolder) => void;
}

// -----------------------------------------------------------------------------
// Utility Types
// -----------------------------------------------------------------------------

export type DocumentTypeLabel = {
  [K in DocumentType]: string;
};

export const DOCUMENT_TYPE_LABELS: DocumentTypeLabel = {
  [DocumentType.MEDICAL_RECORD]: 'Medical Record',
  [DocumentType.IEP]: 'IEP Document',
  [DocumentType.LEGAL]: 'Legal Document',
  [DocumentType.INSURANCE]: 'Insurance',
  [DocumentType.FINANCIAL]: 'Financial',
  [DocumentType.THERAPY]: 'Therapy',
  [DocumentType.SCHOOL]: 'School',
  [DocumentType.OTHER]: 'Other',
};

export const DOCUMENT_TYPE_COLORS: DocumentTypeLabel = {
  [DocumentType.MEDICAL_RECORD]: '#7A9E7E', // sage
  [DocumentType.IEP]: '#6B8DD6', // blue
  [DocumentType.LEGAL]: '#2B3A4D', // navy
  [DocumentType.INSURANCE]: '#C4785A', // terracotta
  [DocumentType.FINANCIAL]: '#8B8178', // warm-gray
  [DocumentType.THERAPY]: '#9DB8A0', // sage-light
  [DocumentType.SCHOOL]: '#445566', // navy-light
  [DocumentType.OTHER]: '#8B8178', // warm-gray
};

export const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
];

export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50MB

export const FILE_TYPE_ICONS: Record<string, string> = {
  'application/pdf': 'FileText',
  'image/jpeg': 'Image',
  'image/png': 'Image',
  'image/gif': 'Image',
  'image/webp': 'Image',
  'application/msword': 'FileText',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'FileText',
  'application/vnd.ms-excel': 'Table',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Table',
  'text/plain': 'File',
  'text/csv': 'Table',
  default: 'File',
};
